import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Image, TagRelationship, Video } from 'generated/graphql';

import { seasonYearFormat } from 'lib/media';

import Grid from '@material-ui/core/Grid';

import { FieldType } from 'components/Showroom/Editor/Steps/Sections/Details/Options/Click/ProductLayer/ShowroomEditorSectionProductLayerOption';

import { useStyles } from './styles';

const prices = ['retailPrices', 'salePrices', 'extraPrices', 'customsPrices'];
const commonFields = ['color', 'fabric', 'pattern', 'aspect'];
const productFields = [
	'barcode',
	'gender',
	'color',
	'fabric',
	'pattern',
	'type',
	'size',
	'sizeRange',
	'description',
	'sampleMadeIn',
	'styleNumber',
	'looks',
	'misc',
	'manufacturer'
];
const descriptionFieldsNoColor = ['fabric', 'aspect', 'pattern', 'styles'];

type ComponentProps = {
	media: Image | Video;
	selectedGalleriesFields: string[];
	selectedSamplesFields: string[];
};

const ProductDetails = (props: ComponentProps) => {
	const { media, selectedGalleriesFields, selectedSamplesFields } = props;

	const classes = useStyles();
	const { t } = useTranslation();

	const displayableTags = React.useMemo(() => {
		return media.tagRelationships.filter(
			rel =>
				rel.target.allowedRelationships?.length > 0 ||
				rel.target.type === 'people_name' ||
				rel.target.associatedTags?.map(tag => tag.key).includes('nature_product')
		);
	}, [media]);

	const { locations, multiDesigner } = React.useMemo(() => {
		return {
			locations: media.tags
				.filter(tag => tag.type === 'LocationTag')
				.map(tag => tag.name)
				.join(', '),
			multiDesigner: media.tags
				.filter(tag => tag.type === 'multi_designer')
				.map(tag => tag.name)
				.join(', ')
		};
	}, [media.tags]);

	const isSampleLinked = React.useMemo(() => {
		return media.tagRelationships.some(rel => rel.isSampleLinked);
	}, [media]);

	const displayField = React.useCallback(
		(fieldKey, fieldValue, fieldType: FieldType) => {
			const selectedFields = fieldType === 'galleries' ? selectedGalleriesFields : selectedSamplesFields;
			const name = fieldType === 'galleries' ? t(`showroom.form.productLayer.galleriesFields.${fieldKey}`) : t(`samplesFields.${fieldKey}`);
			if (!selectedFields.includes(fieldKey) || !fieldValue) return;
			return (
				<Grid item container className={classes.fieldContainer} id={`productField_${fieldKey}`} key={`productField_${fieldKey}`}>
					<span>
						{name} :{' '}
						<span className={classes.fieldValue} id={`productFieldValue_${fieldKey}`}>
							{fieldValue}
						</span>
					</span>
				</Grid>
			);
		},
		[classes, selectedGalleriesFields, selectedSamplesFields, t]
	);

	const displaySampleFields = React.useCallback(
		(fields: string[]) => {
			if (!media?.sampleInfo) return;
			const availableFields = fields.filter(field => selectedSamplesFields.includes(field) && media.sampleInfo[field]);
			if (availableFields.length === 0) return;
			return (
				<Grid container className={clsx(classes.sampleSection)} direction="row" id={`mediaSampleFields`}>
					{availableFields.map(field => {
						if (prices.includes(field) && media.sampleInfo[field]) {
							const priceWithValues = media.sampleInfo[field].filter(price => !!price.value);
							if (priceWithValues?.length > 0) {
								const prices: string = priceWithValues.map(price => `${price.value}${price.currency}`).join(', ');
								return displayField(field, prices, 'samples');
							}
							return;
						} else if (field === 'looks') {
							const value = media.sampleInfo[field].join(', ');
							return displayField(field, value, 'samples');
						} else {
							return displayField(field, media.sampleInfo[field], 'samples');
						}
					})}
				</Grid>
			);
		},
		[media, classes, displayField, selectedSamplesFields]
	);

	const handleDisplayCommonField = React.useCallback(
		(fieldKey: string, fieldValue: string, isSampleLinked: boolean) => {
			if (
				!media?.sampleInfo ||
				!isSampleLinked ||
				!selectedSamplesFields.includes(fieldKey) ||
				(selectedGalleriesFields.includes(fieldKey) && fieldValue)
			)
				return displayField(fieldKey, fieldValue, 'galleries');
			else return displayField(fieldKey, media?.sampleInfo[fieldKey], 'samples');
		},
		[media, displayField, selectedGalleriesFields, selectedSamplesFields]
	);

	const joinDescriptionTag = React.useCallback((relationship: TagRelationship, type: string): string => {
		return relationship.relations
			.filter(tag => tag.type === type)
			.map(tag => tag.name)
			.join(', ');
	}, []);

	return (
		<>
			<Grid container className={classes.fieldSection} direction="row" id={`mediaProductGeneralInfo`}>
				{selectedGalleriesFields.includes('library')
					? displayField('library', media.library?.name, 'galleries')
					: displaySampleFields(['designer'])}
				{displayField('multiDesigner', multiDesigner, 'galleries')}
				{handleDisplayCommonField('season', media.libraryFolders?.season?.name, true)}
				{handleDisplayCommonField('collection', media.libraryFolders?.collection?.name, true)}
				{displayField('seasonYear', seasonYearFormat(media?.seasonYear), 'galleries')}
				{displayField('location', locations, 'galleries')}
				{displaySampleFields(['department'])}
			</Grid>
			{selectedGalleriesFields.includes('product') &&
				displayableTags.map(relationship => {
					// Since colors have subcategories we need to check on the parent too
					const colorTags = relationship.relations.filter(
						({ type, parent: { type: parentType } }) => type === 'color' || parentType === 'color'
					);
					return (
						<Grid
							container
							className={classes.fieldSection}
							direction="column"
							id={`mediaProduct_${relationship.id}`}
							key={`mediaProduct_${relationship.id}`}
						>
							<div className={classes.productTypeField} id={`mediaProductName_${relationship.target.name}`}>
								{relationship.target.name.toUpperCase()}
							</div>
							<div className={classes.productDecscriptionWrapper}>
								{handleDisplayCommonField('color', colorTags.map(tag => tag.name).join(', '), relationship.isSampleLinked)}
								{descriptionFieldsNoColor.map(field =>
									handleDisplayCommonField(field, joinDescriptionTag(relationship, field), relationship.isSampleLinked)
								)}
								{relationship.isSampleLinked && (
									<>
										{displaySampleFields([...productFields.filter(field => !commonFields.includes(field)), ...prices])}
										{displaySampleFields(['vendor', 'vendorCode', 'vendorDate'])}
									</>
								)}
							</div>
						</Grid>
					);
				})}
			{media.isSample && !isSampleLinked && (
				<Grid container className={classes.fieldSection} direction="column" id={`mediaProduct_sampleInfo`}>
					{selectedSamplesFields.includes('sampleType') && media?.sampleInfo?.sampleType && (
						<div className={classes.productTypeField} id={'sampleField_sampleType'}>
							{media.sampleInfo.sampleType.toUpperCase()}
						</div>
					)}
					<div className={classes.productDecscriptionWrapper}>
						{displaySampleFields([...productFields, ...prices])}
						{displaySampleFields(['vendor', 'vendorCode', 'vendorDate'])}{' '}
					</div>
				</Grid>
			)}
		</>
	);
};

export default React.memo(ProductDetails);
